import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  ButtonBase,
  Collapse,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import IosShare from "@mui/icons-material/IosShare";
import { ReactComponent as ShuffleIcon } from "../../Assets/shuffle1.svg";
import { useGetAllUsersQuery } from "../../Slices/roomNateApis";
import { getEventInfo } from "./eventDict";
import { ANALYTICS_FONT_FAMILY } from "./analyticsTypography";
import { ANALYTICS_GREEN, ANALYTICS_RED } from "./analyticsColors";
import { isValidGalleryUrl } from "./FeedList";
import {
  buildSubmissionGroups,
  getSubmissionGroupStatus,
} from "./submissionStats";
import {
  buildSignupUserDirectory,
  matchSignupsToUsersByTime,
  resolveAccountCreatedIdentity,
  rowsForAccountCreated,
  isGenericMetaUser,
  accountCreatedDisplayHeadline,
  formatActivityIdentityLabel,
} from "./accountCreatedEnrichment";
import { resolveSignupFloorPlanOrigin } from "./signupFloorPlanOrigin";
import { humanizeUploadFailure } from "./uploadFailureLabels";
import BottomSheet from "./BottomSheet";
import DrilldownSectionHeader from "./DrilldownSectionHeader";
import DrilldownAboutSection from "./DrilldownAboutSection";
import MetricScopeDrilldownNote from "./MetricScopeDrilldownNote";
import { applyMetricScopeFilters } from "./metricScopeFilters";
import { getMetricScopeForEvent } from "../../utils/eventAttribution";
import { fetchAllOccurrencesForEvent } from "./analyticsTimeseriesFetch";
import { buildVisitorGroups } from "./visitorIdentity";
import { isShareEvent } from "./shareEvents";

// Events that show the 3-column gallery-link row layout in OccurrenceRow.
const GALLERY_ROW_EVENTS = new Set(["webview_opened", "shuffle_room", "link_copied", "link_shared", "download_layout"]);

// Per-event icon rendered in the left column for GALLERY_ROW_EVENTS rows.
const EVENT_ICON = {
  webview_opened: <VisibilityOutlined sx={{ fontSize: 14, color: "rgba(0,0,0,0.4)" }} />,
  shuffle_room:   <ShuffleIcon style={{ width: 13, height: 13, opacity: 0.4 }} />,
  link_copied:    <IosShare sx={{ fontSize: 14, color: "rgba(0,0,0,0.4)" }} />,
  link_shared:    <IosShare sx={{ fontSize: 14, color: "rgba(0,0,0,0.4)" }} />,
  download_layout: <FileDownloadOutlinedIcon sx={{ fontSize: 14, color: "rgba(0,0,0,0.4)" }} />,
};

// Extract the gallery ID (last path segment) from a full gallery URL.
const galleryIdFromUrl = (url) => {
  if (!url) return null;
  try {
    const seg = url.split("/").filter(Boolean).pop();
    return seg || null;
  } catch {
    return null;
  }
};

// Per-event "Load more" page size. floorplan_uploaded reveals more per page
// because the submissions list is a primary review surface; everything else
// keeps the original 5 so first-paint stays cheap.
const PAGE_SIZE_BY_EVENT = { floorplan_uploaded: 10, default: 5 };
const pageSizeFor = (eventKey) => PAGE_SIZE_BY_EVENT[eventKey] ?? PAGE_SIZE_BY_EVENT.default;

const formatTimestamp = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const truncateUrl = (url, max = 42) => {
  if (!url) return "—";
  try {
    const clean = url.replace(/^https?:\/\//, "");
    return clean.length > max ? clean.slice(0, max) + "…" : clean;
  } catch {
    return url;
  }
};

const userLabel = (row) => {
  const u = row.meta?.user;
  if (!u || u === "guest" || u === "anonymous") return "Guest";
  return u;
};

const galleryUrlFor = (row) => {
  if (row.detail?.galleryUrl) return row.detail.galleryUrl;
  if (row.detail?.url) return row.detail.url;
  const id = row.detail?.uniqueId || row.detail?.galleryId;
  if (id) {
    try {
      return `${window.location.origin}/floor-gallery/${id}`;
    } catch {
      return null;
    }
  }
  return null;
};

const lastFourOf = (s) => (s ? String(s).slice(-4) : "");

const submissionFileName = (uploadEvent, failedEvent) =>
  uploadEvent?.detail?.fileName?.trim() ||
  failedEvent?.detail?.fileName?.trim() ||
  null;

const truncatedIdSuffix = (id) => {
  const tail = lastFourOf(id);
  return tail ? `(…${tail})` : "";
};

/**
 * Header line for any activity row — name/email when we know who, otherwise
 * a "Guest · …b9fc" line derived from the last-4 of the guest session key.
 *
 * Exported so the Unique Visitors drill-down and the consolidated Shares /
 * Downloads drill-down render the same person tile across the dashboard.
 */
export const UserHeader = ({ row, allRows, signupUserDirectory }) => {
  const { email, name } = resolveAccountCreatedIdentity(
    row,
    allRows,
    signupUserDirectory,
    undefined
  );
  if (name || email) {
    return (
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: "rgba(0,0,0,0.85)",
            lineHeight: 1.25,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {name || email}
        </Typography>
        {name && email && (
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "rgba(0,0,0,0.5)",
              lineHeight: 1.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email}
          </Typography>
        )}
      </Box>
    );
  }
  const sessionKey = row?.meta?.guestSessionId || row?.meta?.user || "";
  const tail = lastFourOf(sessionKey);
  return (
    <Typography
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 600,
        color: "rgba(0,0,0,0.7)",
        lineHeight: 1.25,
      }}
    >
      Guest{tail ? ` · …${tail}` : ""}
    </Typography>
  );
};

/**
 * A single row in the submission drill-down list — 3 columns:
 *   left:   status dot (green=success, red=need attention, gray=pending)
 *   middle: file name + id suffix or "(Needs Attention)", by-line, email, timestamp
 *   right:  OpenInNew icon for completed rows with a gallery URL
 */
export const SubmissionGroupRow = ({ group, idx, allRows, signupUserDirectory }) => {
  const uploadEvent  = group.rows.find((r) => r.meta?.event === "floorplan_uploaded");
  const successEvent = group.rows.find((r) => r.meta?.event === "gallery_ready");
  const failedEvent  = group.rows.find((r) => r.meta?.event === "upload_failed");

  const status = getSubmissionGroupStatus(group);
  const dotColor =
    status === "success" ? ANALYTICS_GREEN :
    status === "failed"  ? ANALYTICS_RED :
    "rgba(0,0,0,0.22)";

  const anchor     = uploadEvent || group.rows[0];
  const ts         = formatTimestamp(anchor?.timestamp);
  const errorMsg   = failedEvent
    ? humanizeUploadFailure(
        failedEvent.detail?.errorType,
        failedEvent.detail?.errorMessage
      )
    : null;

  const rawUrl     = group.galleryUrl || successEvent?.detail?.galleryUrl;
  const galleryUrl = rawUrl && isValidGalleryUrl(rawUrl) ? rawUrl : null;
  const galleryId  = galleryIdFromUrl(galleryUrl);
  const fileName   = submissionFileName(uploadEvent, failedEvent);
  const displayFileName = fileName || "Floor plan";

  const identity = resolveAccountCreatedIdentity(
    anchor,
    allRows,
    signupUserDirectory
  );
  const { email, name } = identity;
  const byLine = `by ${formatActivityIdentityLabel({ ...identity, row: anchor })}`;

  const borderTop  = idx === 0 ? "none" : "1px solid rgba(0,0,0,0.06)";

  // Success rows open their gallery (unchanged). Failed rows fall back to the
  // captured original artifact (detail.uploadedFileUrl, surfaced by the backend
  // capture join) when present — wrong-type/oversize files included. Null-safe:
  // with no uploadedFileUrl, failed rows stay inert exactly as before.
  const previewUrl =
    galleryUrl ||
    (status === "failed" ? failedEvent?.detail?.uploadedFileUrl || null : null);

  const handleOpen = previewUrl
    ? (e) => { e.preventDefault(); window.open(previewUrl, "_blank", "noopener,noreferrer"); }
    : undefined;

  const titleLineSx = {
    fontSize: "0.78rem",
    lineHeight: 1.4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  };

  return (
    <ButtonBase
      onClick={handleOpen}
      component="div"
      disabled={!previewUrl}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        textAlign: "left",
        px: 2,
        py: 1.25,
        borderTop,
        gap: 1.25,
        cursor: previewUrl ? "pointer" : "default",
        borderRadius: "0 !important",
        textTransform: "none !important",
        "&:hover": previewUrl ? { bgcolor: "rgba(0,0,0,0.02)" } : {},
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          bgcolor: dotColor,
          flexShrink: 0,
          mt: "5px",
        }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {status === "failed" ? (
          <Typography sx={titleLineSx}>
            <Box component="span" sx={{ fontWeight: 700, color: "#000" }}>
              {displayFileName}
            </Box>
          </Typography>
        ) : status === "success" ? (
          <Typography sx={titleLineSx}>
            <Box component="span" sx={{ fontWeight: 700, color: "#000" }}>
              {displayFileName}
            </Box>
            {galleryId && (
              <Box
                component="span"
                sx={{
                  fontWeight: 400,
                  color: "rgba(0,0,0,0.55)",
                  fontFamily: ANALYTICS_FONT_FAMILY,
                }}
              >
                {" "}
                {truncatedIdSuffix(galleryId)}
              </Box>
            )}
          </Typography>
        ) : (
          <Typography
            sx={{
              ...titleLineSx,
              fontWeight: 500,
              color: "rgba(0,0,0,0.4)",
            }}
          >
            {displayFileName}
            {galleryId ? ` ${truncatedIdSuffix(galleryId)}` : " — Processing…"}
          </Typography>
        )}

        {status === "failed" && errorMsg && (
          <Typography
            sx={{
              fontSize: "0.68rem",
              fontWeight: 500,
              color: ANALYTICS_RED,
              lineHeight: 1.35,
              mt: 0.25,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {errorMsg}
          </Typography>
        )}

        <Typography
          sx={{
            fontSize: "0.72rem",
            color: "rgba(0,0,0,0.5)",
            lineHeight: 1.35,
            mt: 0.35,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {byLine}
        </Typography>
        {name && email && (
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "rgba(0,0,0,0.5)",
              lineHeight: 1.35,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {email}
          </Typography>
        )}
        <Typography
          sx={{ fontSize: "0.65rem", color: "rgba(0,0,0,0.32)", lineHeight: 1.4, mt: 0.15 }}
        >
          {ts}
        </Typography>
      </Box>

      <Box sx={{ flexShrink: 0, mt: "3px", opacity: previewUrl ? 1 : 0 }}>
        <OpenInNewIcon sx={{ fontSize: 13, color: "rgba(0,0,0,0.28)" }} />
      </Box>
    </ButtonBase>
  );
};

/**
 * Activity row — used by the gallery-event drill-downs (views / shuffles /
 * shares / downloads). Extracted so the consolidated Shares + Downloads
 * sheet can reuse it without duplicating the gallery-URL fallback logic.
 *
 * The header is delegated to <UserHeader/> so guest rendering matches the
 * Unique Visitors drill-down exactly.
 */
export const ActivityRow = ({
  row,
  idx,
  eventKey,
  allRows,
  signupUserDirectory,
  hideEventIcon = false,
}) => {
  const gUrl = galleryUrlFor(row);
  const ts = formatTimestamp(row.timestamp);
  const borderTop = idx === 0 ? "none" : "1px solid rgba(0,0,0,0.06)";

  let validUrl = gUrl && isValidGalleryUrl(gUrl) ? gUrl : null;
  if (!validUrl) {
    const layoutId = row.detail?.galleryId || row.detail?.uniqueId;
    if (layoutId && Array.isArray(allRows)) {
      const galleryReadyRow = allRows.find(
        (r) =>
          r.meta?.event === "gallery_ready" &&
          (r.detail?.uniqueId === layoutId || r.detail?.galleryId === layoutId)
      );
      const candidate = galleryReadyRow?.detail?.galleryUrl;
      if (candidate && isValidGalleryUrl(candidate)) validUrl = candidate;
    }
  }
  const galleryId = galleryIdFromUrl(validUrl);
  const icon = EVENT_ICON[eventKey] ?? null;

  const handleOpen = validUrl
    ? () => window.open(validUrl, "_blank", "noopener,noreferrer")
    : undefined;

  return (
    <ButtonBase
      onClick={handleOpen}
      component="div"
      disabled={!validUrl}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        width: "100%",
        textAlign: "left",
        px: 2,
        py: 1.25,
        borderTop,
        gap: 1.25,
        cursor: validUrl ? "pointer" : "default",
        borderRadius: "0 !important",
        textTransform: "none !important",
        "&:hover": validUrl ? { bgcolor: "rgba(0,0,0,0.02)" } : {},
      }}
    >
      {!hideEventIcon && (
        <Box sx={{ flexShrink: 0, mt: "4px", display: "flex", alignItems: "center" }}>
          {icon}
        </Box>
      )}

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <UserHeader row={row} allRows={allRows} signupUserDirectory={signupUserDirectory} />
        {galleryId && (
          <Typography
            sx={{
              fontSize: "0.72rem",
              color: "rgba(0,0,0,0.55)",
              fontFamily: ANALYTICS_FONT_FAMILY,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.4,
              mt: 0.25,
            }}
          >
            {galleryId}
          </Typography>
        )}
        <Typography
          sx={{ fontSize: "0.65rem", color: "rgba(0,0,0,0.32)", lineHeight: 1.4, mt: 0.15 }}
        >
          {ts}
        </Typography>
      </Box>

      <Box sx={{ flexShrink: 0, mt: "3px", opacity: validUrl ? 1 : 0 }}>
        <OpenInNewIcon sx={{ fontSize: 13, color: "rgba(0,0,0,0.28)" }} />
      </Box>
    </ButtonBase>
  );
};

const STAT_CHIP_LABELS = [
  { key: "shuffleCount", label: "Shuffles" },
  { key: "shareCount", label: "Shares" },
  { key: "downloadCount", label: "Downloads" },
];

/**
 * One row per unique visitor in the Unique Visitors drill-down. Same chip
 * style as the account_created row so the dashboard reads consistently.
 */
const VisitorRow = ({ group, idx, allRows, signupUserDirectory }) => {
  const borderTop = idx === 0 ? "none" : "1px solid rgba(0,0,0,0.06)";
  const anchor = group.anchorRow || {};
  const lastGallery = group.lastGalleryUrl || null;

  return (
    <Box sx={{ px: 2, py: 1.5, borderTop }}>
      <Stack direction="row" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <UserHeader row={anchor} allRows={allRows} signupUserDirectory={signupUserDirectory} />
        </Box>
        {group.isReturning && (
          <Box
            sx={{
              px: 0.75,
              py: 0.25,
              bgcolor: "rgba(99,102,241,0.08)",
              borderRadius: "6px",
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.65rem",
                fontWeight: 600,
                color: "rgba(99,102,241,0.92)",
                lineHeight: 1.3,
                whiteSpace: "nowrap",
              }}
            >
              Returning
            </Typography>
          </Box>
        )}
      </Stack>
      <Stack
        direction="row"
        sx={{ mt: 0.75, flexWrap: "wrap", rowGap: 0.5, gap: 0.75 }}
      >
        {STAT_CHIP_LABELS.map(({ key, label }) => {
          const val = group[key] ?? 0;
          return (
            <Box
              key={key}
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: "rgba(0,0,0,0.04)",
                borderRadius: "6px",
                display: "flex",
                gap: 0.5,
                alignItems: "baseline",
                opacity: val === 0 ? 0.5 : 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: "rgba(0,0,0,0.75)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {val === 0 ? "—" : val.toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: "0.66rem", color: "rgba(0,0,0,0.45)" }}>
                {label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
      {lastGallery && (
        <Link
          href={lastGallery}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          underline="hover"
          sx={{
            display: "block",
            mt: 0.75,
            fontSize: "0.72rem",
            fontFamily: ANALYTICS_FONT_FAMILY,
            color: "#6366f1",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }}
        >
          {galleryIdFromUrl(lastGallery) || truncateUrl(lastGallery, 36)}
        </Link>
      )}
      {group.firstSeen && (
        <Typography
          sx={{ fontSize: "0.65rem", color: "rgba(0,0,0,0.32)", lineHeight: 1.4, mt: 0.5 }}
        >
          {group.opens > 1
            ? `${group.opens} opens${
                group.isReturning && group.sessionCount > 1
                  ? ` across ${group.sessionCount} visits`
                  : ""
              } · last ${formatTimestamp(group.lastSeen)}`
            : `Opened ${formatTimestamp(group.firstSeen)}`}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Renders a single recent-occurrence row with fields appropriate for the
 * event type. Falls back gracefully for unknown events.
 *
 * For webview_opened / shuffle_room / link_copied the same 3-column layout
 * as SubmissionGroupRow is used: event icon | gallery ID + by user + ts | ↗
 */
const OccurrenceRow = ({
  row,
  idx,
  eventKey,
  allRows,
  signupUserDirectory,
  signupTimeMatchMap,
  catalogLayouts = [],
  feedActivityReady = true,
}) => {
  const gUrl = galleryUrlFor(row);
  const user = userLabel(row);
  const ts = formatTimestamp(row.timestamp);

  const borderTop = idx === 0 ? "none" : "1px solid rgba(0,0,0,0.06)";

  if (GALLERY_ROW_EVENTS.has(eventKey)) {
    // Reuse the shared row so the gallery-event branch is one implementation.
    return (
      <ActivityRow
        row={row}
        idx={idx}
        eventKey={eventKey}
        allRows={allRows}
        signupUserDirectory={signupUserDirectory}
      />
    );
  }

  if (eventKey === "account_created") {
    const { email, name } = resolveAccountCreatedIdentity(
      row,
      allRows,
      signupUserDirectory,
      signupTimeMatchMap
    );
    const userRows = rowsForAccountCreated(row, allRows, email);
    const uploads = userRows.filter((r) => r.meta?.event === "floorplan_uploaded").length;
    const shuffles = userRows.filter((r) => r.meta?.event === "shuffle_room").length;
    const shares = userRows.filter((r) => isShareEvent(r.meta?.event)).length;
    const downloads = userRows.filter((r) => r.meta?.event === "download_layout").length;

    const headline = accountCreatedDisplayHeadline({
      name,
      email,
      metaUser: row.meta?.user,
    });
    const subline =
      name && email
        ? email
        : !name && email
        ? null
        : isGenericMetaUser(row.meta?.user)
        ? null
        : row.meta?.user;
    const showEmailLine = Boolean(email) && headline !== email;
    const origin = resolveSignupFloorPlanOrigin(row, allRows, catalogLayouts);

    return (
      <Box
        key={row._id || idx}
        sx={{ px: 2, py: 1.5, borderTop }}
      >
        <Typography
          sx={{ fontSize: "0.875rem", fontWeight: 600, color: "rgba(0,0,0,0.88)" }}
          noWrap
        >
          {headline}
        </Typography>
        {(subline || showEmailLine) && (
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: "rgba(0,0,0,0.55)",
              mt: 0.2,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {showEmailLine ? email : subline}
          </Typography>
        )}
        {origin.kind === 'floor_plan' && origin.galleryUrl && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
            {origin.plan && (
              <Avatar
                variant="rounded"
                src={origin.plan.thumbnail || undefined}
                sx={{ width: 32, height: 32, borderRadius: '6px', flexShrink: 0 }}
              >
                {origin.plan.name?.[0] || 'F'}
              </Avatar>
            )}
            <Link
              href={origin.galleryUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                fontSize: '0.75rem',
                fontFamily: ANALYTICS_FONT_FAMILY,
                color: '#6366f1',
                fontWeight: 500,
                wordBreak: 'break-all',
                lineHeight: 1.35,
              }}
            >
              {origin.galleryUrl}
            </Link>
          </Stack>
        )}
        {/* /signup branch renders the URL as a clickable indigo link, same
            shape as the floor_plan branch above — only the avatar drops. */}
        {origin.kind === 'signup_page' && origin.galleryUrl && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
            <Link
              href={origin.galleryUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.4,
                fontSize: '0.75rem',
                fontFamily: ANALYTICS_FONT_FAMILY,
                color: '#6366f1',
                fontWeight: 500,
                wordBreak: 'break-all',
                lineHeight: 1.35,
              }}
            >
              {origin.galleryUrl}
            </Link>
          </Stack>
        )}
        {(origin.kind === 'floor_plan' || origin.kind === 'signup_page') && (
          <Typography
            variant="caption"
            sx={{ display: "block", color: "rgba(0,0,0,0.38)", fontSize: "0.68rem", mt: 0.35 }}
          >
            Signed up {ts}
          </Typography>
        )}
        {feedActivityReady ? (
          <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
            {[
              { label: "Uploads", val: uploads },
              { label: "Shuffles", val: shuffles },
              { label: "Shares", val: shares },
              { label: "Downloads", val: downloads },
            ].map(({ label, val }) => (
              <Box
                key={label}
                sx={{
                  px: 1,
                  py: 0.25,
                  bgcolor: "rgba(0,0,0,0.04)",
                  borderRadius: "6px",
                  display: "flex",
                  gap: 0.5,
                  alignItems: "baseline",
                  opacity: val === 0 ? 0.5 : 1,
                }}
              >
                <Typography sx={{ fontSize: "0.7rem", fontWeight: 600, color: "rgba(0,0,0,0.75)" }}>
                  {val === 0 ? "—" : val}
                </Typography>
                <Typography sx={{ fontSize: "0.66rem", color: "rgba(0,0,0,0.45)" }}>
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography
            sx={{
              fontSize: "0.68rem",
              color: "rgba(0,0,0,0.42)",
              fontStyle: "italic",
              mt: 1,
              lineHeight: 1.4,
            }}
          >
            Activity stats appear when the full period finishes loading.
          </Typography>
        )}
      </Box>
    );
  }

  // Default: fallback for any event not covered above
  return (
    <Box key={row._id || idx} sx={{ px: 2, py: 1.25, borderTop }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.75)", fontSize: "0.8rem", fontWeight: 500 }}>
          {ts}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(0,0,0,0.45)",
            fontSize: "0.72rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {user}
        </Typography>
      </Stack>
      {gUrl && isValidGalleryUrl(gUrl) && (
        <Link
          href={gUrl}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          sx={{
            display: "block",
            mt: 0.35,
            fontSize: "0.72rem",
            color: "rgba(0,0,0,0.5)",
            fontFamily: ANALYTICS_FONT_FAMILY,
            textTransform: "none !important",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {truncateUrl(gUrl)}
        </Link>
      )}
    </Box>
  );
};

const formatProcessingTime = (secs) => {
  if (secs == null) return null;
  if (secs < 60) return `~${secs}s`;
  return `~${Math.round(secs / 60)}m`;
};

const MetricDrillModal = ({
  open,
  onClose,
  metric,
  rows,
  contextRows = [],
  excludeInternal = false,
  excludePartnerAgents = false,
  partnerAgentEmailDomain = null,
  partnerAdminUsers = [],
  userDirectory = null,
  loading = false,
  dateLabel,
  dateParams = "",
  partnerNameFilter = "",
  usersPartnerFilter = null,
  avgProcessingSeconds = null,
  conversionRate = null,
  effectivePrompted = null,
  catalogLayouts = [],
  feedActivityReady = true,
  feedCapped = false,
  feedEventCap = 7500,
  submissionSourceRows = null,
  submissionSourceReady = false,
}) => {
  const [occOpen, setOccOpen] = useState(true);
  const eventKey = metric?.eventKey;
  const initialPageSize = pageSizeFor(eventKey);
  const [visibleCount, setVisibleCount] = useState(initialPageSize);
  const [supplementalRowsRaw, setSupplementalRowsRaw] = useState([]);
  const [supplementalLoading, setSupplementalLoading] = useState(false);
  const supplementalReqRef = useRef(0);

  const metricScope = useMemo(
    () => getMetricScopeForEvent(eventKey),
    [eventKey]
  );

  const scopeFilterOpts = useMemo(
    () => ({
      scope: metricScope,
      excludeInternal,
      excludeAgentActivity: excludePartnerAgents,
      partnerHostname: partnerNameFilter,
      partnerAdminUsers,
      contextRows,
    }),
    [
      metricScope,
      excludeInternal,
      excludePartnerAgents,
      partnerNameFilter,
      partnerAdminUsers,
      contextRows,
    ]
  );

  const filterDrilldownRows = useCallback(
    (rawRows) => applyMetricScopeFilters(rawRows, scopeFilterOpts),
    [scopeFilterOpts]
  );

  const supplementalRows = useMemo(
    () => filterDrilldownRows(supplementalRowsRaw),
    [supplementalRowsRaw, filterDrilldownRows]
  );

  const info = eventKey ? getEventInfo(eventKey) : null;

  // Special path for the Submissions card: group all feed rows by upload
  // session so each row can show its outcome (success / failed / pending).
  // Only active when eventKey is 'floorplan_uploaded'.
  const isSubmissionView = eventKey === "floorplan_uploaded";
  // Unique Visitors view is a parallel branch of webview_opened — we only
  // enable it when the tile explicitly opts in via metric.uniqueVisitorsView.
  // This keeps any future caller that wants the plain webview_opened list
  // unaffected (none exists today, but the guard is cheap).
  const isUniqueVisitorsView =
    eventKey === "webview_opened" && metric?.uniqueVisitorsView === true;
  const hideHeroSummary =
    isSubmissionView || isUniqueVisitorsView || eventKey === "account_created";

  const feedFiltered = useMemo(() => {
    if (!Array.isArray(rows) || !eventKey) return [];
    return rows
      .filter((r) => r.meta?.event === eventKey)
      .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  }, [rows, eventKey]);

  // When /counts says there are N events but the paginated feed slice has
  // fewer (common for account_created), load the full-window list here.
  // Submissions ALSO use this path so the per-event count is honest even
  // when the partner has more uploads than fit in the feed slice.
  const submissionGroupsFromFeed = useMemo(() => {
    if (!isSubmissionView) return null;
    return buildSubmissionGroups(Array.isArray(rows) ? rows : []);
  }, [isSubmissionView, rows]);

  const submissionGroupsFromSource = useMemo(() => {
    if (!isSubmissionView || !submissionSourceReady) return null;
    if (!Array.isArray(submissionSourceRows)) return null;
    return buildSubmissionGroups(submissionSourceRows);
  }, [isSubmissionView, submissionSourceReady, submissionSourceRows]);

  useEffect(() => {
    if (!open || !eventKey) {
      setSupplementalRowsRaw([]);
      setSupplementalLoading(false);
      return undefined;
    }
    if (isSubmissionView && submissionSourceReady) {
      setSupplementalRowsRaw([]);
      setSupplementalLoading(false);
      return undefined;
    }
    const expected = Number(metric?.value) || 0;
    if (isSubmissionView) {
      const groupsLen = submissionGroupsFromFeed?.length ?? 0;
      if (!feedCapped && groupsLen >= expected) {
        setSupplementalRowsRaw([]);
        setSupplementalLoading(false);
        return undefined;
      }
    } else if (feedFiltered.length >= expected) {
      setSupplementalRowsRaw([]);
      setSupplementalLoading(false);
      return undefined;
    }
    if (!dateParams || !partnerNameFilter) return undefined;

    const reqId = ++supplementalReqRef.current;
    setSupplementalLoading(true);
    fetchAllOccurrencesForEvent(eventKey, dateParams, partnerNameFilter)
      .then((fetched) => {
        if (reqId !== supplementalReqRef.current) return;
        setSupplementalRowsRaw(fetched);
      })
      .catch(() => {
        if (reqId !== supplementalReqRef.current) return;
        setSupplementalRowsRaw([]);
      })
      .finally(() => {
        if (reqId === supplementalReqRef.current) setSupplementalLoading(false);
      });

    return () => {
      supplementalReqRef.current += 1;
    };
  }, [
    open,
    eventKey,
    isSubmissionView,
    metric?.value,
    feedFiltered.length,
    submissionGroupsFromFeed?.length,
    submissionSourceReady,
    feedCapped,
    dateParams,
    partnerNameFilter,
  ]);

  const occurrenceRows = useMemo(() => {
    if (isSubmissionView) return [];
    const byId = new Map();
    [...feedFiltered, ...supplementalRows].forEach((r) => {
      const id = r._id || `${r.timestamp}-${r.meta?.user}`;
      if (!byId.has(id)) byId.set(id, r);
    });
    return [...byId.values()].sort(
      (a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0)
    );
  }, [isSubmissionView, feedFiltered, supplementalRows]);

  const allRowsForStats = useMemo(
    () => [...(Array.isArray(rows) ? rows : []), ...supplementalRows],
    [rows, supplementalRows]
  );

  const needsSignupUsers = open && eventKey === "account_created";
  const usersQueryArg =
    !usersPartnerFilter || usersPartnerFilter === "all" ? null : usersPartnerFilter;
  const { data: signupUsersRaw, isFetching: signupUsersLoading } = useGetAllUsersQuery(
    usersQueryArg,
    { skip: !needsSignupUsers }
  );

  const signupUserDirectory = useMemo(() => {
    const users =
      Array.isArray(partnerAdminUsers) && partnerAdminUsers.length > 0
        ? partnerAdminUsers
        : signupUsersRaw;
    return buildSignupUserDirectory(users);
  }, [partnerAdminUsers, signupUsersRaw]);

  const signupTimeMatchMap = useMemo(() => {
    if (eventKey !== "account_created") return new Map();
    return matchSignupsToUsersByTime(occurrenceRows, signupUserDirectory);
  }, [eventKey, occurrenceRows, signupUserDirectory]);

  const submissionGroups = useMemo(() => {
    if (!isSubmissionView) return null;
    if (submissionGroupsFromSource) return submissionGroupsFromSource;
    const merged = [...(Array.isArray(rows) ? rows : []), ...supplementalRows];
    return buildSubmissionGroups(merged);
  }, [isSubmissionView, submissionGroupsFromSource, rows, supplementalRows]);

  // Identity directory for the unique-visitors view. Prefer the directory the
  // KPI tile computed its count from so the drill-down resolves the exact same
  // visitors — the headline and this list are now one computation.
  const visitorDirectory = userDirectory ?? signupUserDirectory;

  // Aggregate per-visitor stats for the Unique Visitors drill-down. Uses the
  // shared buildVisitorGroups helper that also powers the KPI count, so "N of N
  // shown" always equals the headline.
  const visitorGroups = useMemo(() => {
    if (!isUniqueVisitorsView) return [];
    // `rows` is already scope-filtered upstream (it's the same demand-rows the
    // KPI tile counts); only the raw supplemental fetch needs filtering. Re-
    // filtering `rows` here risked drifting from the headline, so we don't.
    const merged = [
      ...(Array.isArray(rows) ? rows : []),
      ...supplementalRows,
    ];
    return buildVisitorGroups(merged, {
      contextRows: Array.isArray(contextRows) ? contextRows : [],
      directory: visitorDirectory,
      galleryUrlFor,
      isValidGalleryUrl,
    });
  }, [isUniqueVisitorsView, rows, supplementalRows, contextRows, visitorDirectory]);

  // Use submission groups for the submissions view, merged occurrence rows otherwise.
  const displayItems = isSubmissionView
    ? (submissionGroups ?? [])
    : isUniqueVisitorsView
    ? visitorGroups
    : occurrenceRows;
  const visible = displayItems.slice(0, visibleCount);
  const hasMore = visibleCount < displayItems.length;
  const headlineCount = Number(metric?.value) || 0;
  const showOccurrencesSection =
    displayItems.length > 0 || supplementalLoading || headlineCount > 0;

  // Reset visible count when modal opens a new metric. Page size now varies
  // per event, so the reset has to use the per-event default — not a constant.
  const handleClose = () => {
    setVisibleCount(pageSizeFor(eventKey));
    setOccOpen(true);
    setSupplementalRowsRaw([]);
    setSupplementalLoading(false);
    onClose();
  };

  const sectionCountLabel = supplementalLoading
    ? "Loading…"
    : `${visible.length} of ${displayItems.length} shown`;

  const recentSectionTitle = isUniqueVisitorsView
    ? "Recent visitors"
    : isSubmissionView
    ? "Recent submissions"
    : eventKey === "account_created"
    ? "Recent sign-ups"
    : "Recent activity";

  return (
    <BottomSheet
      open={open}
      onClose={handleClose}
      title={metric?.title || "Metric"}
      subtitle={dateLabel}
    >
      <MetricScopeDrilldownNote
        scope={metricScope}
        excludeInternal={excludeInternal}
        excludePartnerAgents={excludePartnerAgents}
        partnerAgentEmailDomain={partnerAgentEmailDomain}
      />
      {feedCapped && eventKey === "account_created" && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "rgba(0,0,0,0.5)",
            lineHeight: 1.5,
            mb: 1,
            px: 0.5,
          }}
        >
          Activity stats in this list use the most recent {Number(feedEventCap).toLocaleString()}{" "}
          events in the period when the full window could not be loaded.
        </Typography>
      )}
      <Stack spacing={3}>
        {!hideHeroSummary && (
        <Box>
          <Typography
            sx={{
              fontSize: { xs: "2.25rem", sm: "2.5rem" },
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: "-0.02em",
              color: "rgba(0,0,0,0.9)",
            }}
          >
            {metric?.value ?? "—"}
          </Typography>
          {metric?.title && (
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 500,
                color: "rgba(0,0,0,0.45)",
                mt: 0.75,
                letterSpacing: 0.1,
              }}
            >
              {metric.title}
            </Typography>
          )}
          {info?.desc && (
            <Box
              sx={{
                mt: 1.25,
                p: "6px 10px",
                borderRadius: "8px",
                bgcolor: "rgba(99,102,241,0.05)",
                border: "1px solid rgba(99,102,241,0.1)",
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: "rgba(0,0,0,0.55)", fontSize: "0.78rem", lineHeight: 1.45 }}
              >
                {info.desc}
              </Typography>
            </Box>
          )}
          {isSubmissionView && avgProcessingSeconds != null && (
            <Typography
              variant="caption"
              sx={{
                display: "block",
                color: "rgba(0,0,0,0.4)",
                fontSize: "0.72rem",
                fontWeight: 400,
                mt: 1,
              }}
            >
              Avg {formatProcessingTime(avgProcessingSeconds)} to process
            </Typography>
          )}
        </Box>
        )}

        {metric?.about && (
          <DrilldownAboutSection
            body={metric.about.body}
            sections={metric.about.sections}
            sheetOpen={open}
            deferContentUntilOpen={eventKey === "account_created"}
          />
        )}

        {showOccurrencesSection && (
          <Box>
            <DrilldownSectionHeader
              title={recentSectionTitle}
              countLabel={sectionCountLabel}
              collapsible
              open={occOpen}
              onToggle={() => setOccOpen((v) => !v)}
            />

            <Collapse in={occOpen}>
              <Box
                sx={{
                  borderRadius: "12px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  bgcolor: "#fff",
                  overflow: "hidden",
                }}
              >
                {(
                  loading ||
                  supplementalLoading ||
                  (eventKey === "account_created" && signupUsersLoading)
                ) && displayItems.length === 0 ? (
                  <Typography
                    sx={{
                      px: 2,
                      py: 2,
                      fontSize: "0.8rem",
                      color: "rgba(0,0,0,0.45)",
                      textAlign: "center",
                    }}
                  >
                    Loading…
                  </Typography>
                ) : displayItems.length === 0 ? (
                  <Typography
                    sx={{
                      px: 2,
                      py: 2,
                      fontSize: "0.8rem",
                      color: "rgba(0,0,0,0.45)",
                      lineHeight: 1.5,
                    }}
                  >
                    {headlineCount > 0
                      ? `We counted ${headlineCount} ${
                          isUniqueVisitorsView ? "visit" : "record"
                        }${headlineCount !== 1 ? "s" : ""} in this window, but could not load the individual records. Try refreshing.`
                      : "No records in this period."}
                  </Typography>
                ) : isSubmissionView ? (
                  visible.map((group, idx) => (
                    <SubmissionGroupRow
                      key={group.key}
                      group={group}
                      idx={idx}
                      allRows={allRowsForStats}
                      signupUserDirectory={signupUserDirectory}
                    />
                  ))
                ) : isUniqueVisitorsView ? (
                  visible.map((group, idx) => (
                    <VisitorRow
                      key={group.key}
                      group={group}
                      idx={idx}
                      allRows={allRowsForStats}
                      signupUserDirectory={visitorDirectory}
                    />
                  ))
                ) : (
                  visible.map((row, idx) => (
                    <OccurrenceRow
                      key={row._id || `${row.timestamp}-${idx}`}
                      row={row}
                      idx={idx}
                      eventKey={eventKey}
                      allRows={allRowsForStats}
                      signupUserDirectory={signupUserDirectory}
                      signupTimeMatchMap={signupTimeMatchMap}
                      catalogLayouts={catalogLayouts}
                      feedActivityReady={feedActivityReady}
                    />
                  ))
                )}

                {hasMore && (
                  <ButtonBase
                    onClick={() => setVisibleCount((n) => n + pageSizeFor(eventKey))}
                    sx={{
                      width: "100%",
                      py: 1.25,
                      borderTop: "1px solid rgba(0,0,0,0.06)",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      color: "rgba(99,102,241,0.9)",
                      borderRadius: "0 0 12px 12px !important",
                      textTransform: "none !important",
                      "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
                    }}
                  >
                    Load more
                  </ButtonBase>
                )}
              </Box>
            </Collapse>
          </Box>
        )}
      </Stack>
    </BottomSheet>
  );
};

export default MetricDrillModal;
